import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ProjectTagsListComponent } from '@app/components/project-tags/project-tags-list/project-tags-list.component';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { ActiveProjectService } from '@app/shared/services/active-project.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { TagsDataService } from '@app/shared/services/tags-data.service';
import { Tag, TagFormDialogData } from '@app/shared/types/models/tag/tag.model';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { UiPageWrapperComponent } from '@app/shared/ui/page-wrapper/ui-page-wrapper.component';
import { UiSpinnerDirective } from '@app/shared/ui/spinner/ui-spinner.directive';
import { filter, switchMap } from 'rxjs';

@Component({
  selector: 'proffeo-project-tags',
  templateUrl: './project-tags-view.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [UiPageWrapperComponent, UiInputComponent, ProjectTagsListComponent, UiButtonComponent, UiSpinnerDirective]
})
export class ProjectTagsViewComponent {
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly tagsService: TagsDataService = inject(TagsDataService);
  private readonly activeProjectService: ActiveProjectService = inject(ActiveProjectService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly projectTags: Signal<Tag[]> = computed(() =>
    this.tagsService.getProjectTags(this.activeProjectService.activeProjectId())()
  );

  protected searchValue: WritableSignal<string | undefined> = signal('');
  protected filteredItems: Signal<Tag[]> = this.projectTags;

  public constructor() {
    if (this.activeProjectService.activeProjectId()) {
      this.tagsService.loadProjectTags(this.activeProjectService.activeProjectId());
    }
    this.filteredItems = computed(() => {
      const trimmedSearch = this.searchValue()?.trim()?.toLowerCase();
      if (trimmedSearch) {
        return this.projectTags()?.filter(tag => tag.name.toLowerCase().includes(trimmedSearch));
      } else {
        return this.projectTags();
      }
    });
  }

  protected openCreateProjectTagDialog(): void {
    const data: TagFormDialogData = {};
    this.dialogService
      .openTagFormDialog(data)
      .afterClosed()
      .pipe(
        filter(result => !!result),
        switchMap(result =>
          this.tagsService.createTag(
            this.activeProjectService.activeProjectId(),
            new Tag({
              ...result,
              projectId: this.activeProjectService.activeProjectId()
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
